import { Grid, Paper, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  buildImageUrl,
  updateMainHeader,
} from "../../../commonFunctions/CommonFunctions";
import http from "../../../commonFunctions/httpClient";
import config from "../../../Config/config.json";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import WSButton from "../../../Components/Button/WSButton";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import WSTextField from "../../../Components/TextBox/WSTextBox";
import WSImageUpload from "../../../Components/FileUpload/WSImageUpload";
import WSBackButton from "../../../Components/BackButton/BackButton";

interface ScanCategoryFormData {
  Name: string;
  imageFile: File | string | null;
}

const ScanCategory: React.FC<any> = React.memo(() => {
  const { showMessage } = useSnackbar();
  const navigate = useNavigate();
  const params = useParams();
  const id: any = params?.id;
  const isEditMode = !!id;

  const [loading, setLoading] = useState<boolean>(false);
  const [existingImage, setExistingImage] = useState<string | undefined>(undefined);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm<ScanCategoryFormData>({ mode: "onChange" });

  useEffect(() => {
    updateMainHeader(isEditMode ? "Update Scan Category" : "Add Scan Category");
    breadCrumbsStore.updateBreadCrumbs([
      { "Scan Category": "/scan-category/list" },
      { [isEditMode ? "Update" : "Add"]: "/" },
    ]);
  }, [isEditMode]);

  // Load existing record for edit  →  GET /api/pharmacy/ScanCategory/{id}
  useEffect(() => {
    const getById = async () => {
      if (!id) return;
      try {
        const response: any = await http.get(`pharmacy/ScanCategory/${id}`);
        const data = response?.data || response;
        if (data) {
          setValue("Name", data.name || "");
          if (data.imgUrl) {
            setExistingImage(buildImageUrl(data.imgUrl, config.imgUrl));
          }
        }
      } catch (error) {
        console.error("Error fetching scan category:", error);
        showMessage("Error fetching scan category", "error");
      }
    };
    getById();
  }, [id, setValue, showMessage]);

  const onSubmit = async (data: ScanCategoryFormData) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("Name", data.Name || "");
      // Only append the image when the user actually selected a new file
      if (data.imageFile instanceof File) {
        formData.append("imageFile", data.imageFile);
      }

      let response: any;
      if (isEditMode && id) {
        // PUT /api/pharmacy/ScanCategory/{id}
        response = await http.putFormData(`pharmacy/ScanCategory/${id}`, formData);
      } else {
        // POST /api/pharmacy/ScanCategory
        response = await http.postFormData("pharmacy/ScanCategory", formData);
      }

      const success =
        response?.success === true ||
        !!response?.id ||
        (!!response && !response?.errors && !response?.title);

      if (success) {
        showMessage(
          response?.message ||
            (isEditMode
              ? "Scan category updated successfully!"
              : "Scan category created successfully!"),
          "success"
        );
        navigate("/scan-category/list");
      } else {
        const errMsg =
          response?.message ||
          (response?.errors ? JSON.stringify(response.errors) : null) ||
          "Failed to save scan category";
        showMessage(errMsg, "error");
      }
    } catch (e: any) {
      console.error("Submit error:", e);
      showMessage(
        e?.response?.data?.message || e?.message || "Failed to save scan category",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => navigate("/scan-category/list");

  return (
    <>
      <WSBackButton id="btnScanCategoryBack" url="/scan-category/list" />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} mt={2}>
          <Grid item xs={12}>
            <Paper
              elevation={0}
              style={{
                paddingLeft: 20,
                paddingRight: 20,
                marginTop: 0,
                paddingBottom: 25,
                borderRadius: "8px",
              }}
            >
              <Grid container spacing={2} mt={0}>
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: "1rem", fontWeight: "bold" }}>
                    Scan Category Details
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <WSImageUpload
                    control={control}
                    id="imageFile"
                    name="imageFile"
                    label="Image"
                    rules={{ required: false }}
                    imgUrl={existingImage}
                  />
                </Grid>

                <Grid item xs={12}>
                  <WSTextField
                    control={control}
                    id="Name"
                    name="Name"
                    rules={{ required: true }}
                    label="Scan Category *"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid container justifyContent="flex-end" spacing={2} sx={{ mt: 2 }}>
            <Grid item>
              <WSButton
                id="cancel"
                type="button"
                label="Cancel"
                size="medium"
                onClick={handleCancelClick}
                variant="outlined"
                color="primary"
                sx={{
                  borderRadius: 8,
                  width: "8rem",
                  border: "1px solid #098963",
                  color: "#098963",
                  backgroundColor: "#FFFFFF",
                }}
              />
            </Grid>
            <Grid item>
              <WSButton
                id="submit"
                type="submit"
                label={isEditMode ? "Update" : "Save"}
                size="medium"
                variant="outlined"
                color="primary"
                loading={loading}
                disabled={!isValid || loading}
                sx={{
                  borderRadius: 8,
                  width: "8rem",
                  border: "1px solid #098963",
                  color: "#FFFFFF",
                  backgroundColor: "#098963",
                  "&:hover": { backgroundColor: "#066e4f" },
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </form>
    </>
  );
});

export default ScanCategory;
